import React, { useEffect, useMemo } from "react";
import { useCategoryOptions, useSubcategoryOptions, useGetRate, usePlanChargesChoices, usePreviewPlanEditDetails, useUtilityServices, useIncludedIn } from "../../../hooks";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/ui/use-toast";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Tag, Plus, Banknote } from "lucide-react";
import { SegmentedControl } from "@shared/components/SegmentedControl";

interface SetValuesProps {
  data: any;
  onDataChange: (data: any) => void;
  onNext: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const SetValues: React.FC<any> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  allStepData,
}) => {
  const selectedFields = allStepData?.[1]?.selectedFields || [];
  console.log("selectedFields", selectedFields);
  

  const form = FormService.createForm({
    fields: [],
    defaultValues: data?.values || {},
    mode: "onChange",
  });

  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const { mutateAsync: previewPlanEditDetails, isPending: isPreviewing } = usePreviewPlanEditDetails();
  const { data: servicesResult } = useUtilityServices();

  const selectedCategory = form.watch("category") || [];
  const selectedCategoryStr = Array.isArray(selectedCategory) ? selectedCategory.join(",") : selectedCategory;
  const selectedCommonService = form.watch("commonService");

  const currency =useCurrency();
    const {data:includedInData}=useIncludedIn();
      const includedInOptions = useMemo(
        () =>
          includedInData?.result?.map((item) => ({
            value: item.key,
            label: item.value,
          })) || [],
        [includedInData]
      );
  const { data: chargesChoicesResult } = usePlanChargesChoices();
  const chargesChoicesOptions = useMemo(() => {
    const dataArray = Array.isArray(chargesChoicesResult) ? chargesChoicesResult : chargesChoicesResult?.result || [];
    return dataArray.map((c: any) => ({
      value: c.key,
      label: c.value
    }));
  }, [chargesChoicesResult]);

  const { data: rateResult } = useGetRate(selectedCommonService);
  const rateOptions = useMemo(() => {
    const dataArray = Array.isArray(rateResult) ? rateResult : rateResult?.result || [];
    return dataArray.map((r: any) => ({
      value: r.id,
      label: r.rateName
    }));
  }, [rateResult]);

  const { data: categoriesResult } = useCategoryOptions();
  const categoriesOptions = useMemo(() => {
    if (!Array.isArray(categoriesResult)) return [];
    return categoriesResult.map((c: any) => ({
      value: c.key || c.id || c.code,
      label: c.value || c.name || c.label || c.key
    }));
  }, [categoriesResult]);

  const { data: subcategoriesResult } = useSubcategoryOptions(selectedCategoryStr);
  const subcategoriesOptions = useMemo(() => {
    if (!Array.isArray(subcategoriesResult)) return [];
    return subcategoriesResult.map((c: any) => ({
      value: c.key || c.id || c.code,
      label: c.value || c.name || c.label || c.key
    }));
  }, [subcategoriesResult]);

  const serviceOptions = useMemo(() => {
        const responseData = servicesResult?.result || servicesResult;
        if (!Array.isArray(responseData)) return [];
        return responseData
          .filter((service: any) => service.isActive === true)
          .map((service: any) => ({
            label: service.name,
            value: service.name,
          }));
      }, [servicesResult]);



  const dynamicFields = useMemo(() => {
    const fields: FormField[] = [];

    if (selectedFields.includes("category")) {
      fields.push({
        name: "category",
        label: "Category",
        type: "multi-select",
        helperText:"Single category each record will be reassigned to.",
        options: categoriesOptions,
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "subcategory",
        label: "Sub Category",
        type: "multi-select",
        helperText: "Sub category each record will be reassigned to.",
        options: subcategoriesOptions,
        classes: FIELD_CLASSES,
      });
    }
    

    if (selectedFields.includes("effectiveFrom")) {
      fields.push({
        name: "effectiveFrom",
        label: "Effective From",
        type: "datepicker",
        helperText:"When the records start applying after the bulk change.",
        classes: FIELD_CLASSES,
      });
    }

    if (selectedFields.includes("endDate")) {
      fields.push({
        name: "endDate",
        label: "Effective To",
        type: "datepicker",
        helperText:"When the records stop applying.",
        classes: FIELD_CLASSES,
      });
    }

    if (selectedFields.includes("setTariff")) {
      const commonServicesData = allStepData?.[1]?.commonServices;
      const commonServicesList = Array.isArray(commonServicesData) 
        ? commonServicesData 
        : (Array.isArray(commonServicesData?.commonServices) 
            ? commonServicesData.commonServices 
            : []);

      fields.push({
        name: "commonService",
        label: "Common Service",
        type: "select",
        group:"Set Tariff for Service",
        groupColorNeeded:false,
        helperText:"Only services present in every selected plan are listed.",
        options: commonServicesList.map((service: string) => ({ label: service, value: service })),
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "tariffToApply",
        label: "Tariff to apply",
        type: "select",
        group:"Set Tariff for Service",
        groupColorNeeded:false,
        helperText:"The chosen tariff replaces each plan's existing tariff for the same service.",
        options: rateOptions,
        classes: FIELD_CLASSES,
      });
    }

    if (selectedFields.includes("addCharge")) {
      fields.push({
        name: "chargeCategory",
        label: "Charge Category",
        type: "select",
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Select the type of charge or tax to add to the selected records.",
        options: chargesChoicesOptions,
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "chargeName",
        label: "Name",
        type: "text",
        placeholder:"Name of charge",
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Enter a descriptive name for this new charge.",
        classes: FIELD_CLASSES,
      });
       fields.push( 
        {
          name: "linkedService", 
          label: "Linked Service", 
          type: "select",
          group:"Add a Charge",
          groupColorNeeded:false,
          helperText: "Select the utility service this charge's value is derived from.",
          options: serviceOptions,
          classes: FIELD_CLASSES,
          showWhen: (formValues) => String(formValues.chargeCategory) === "0",
        });
      fields.push({
         name: "usage", 
         label: "Usage (%)", 
         type: "number", 
         group:"Add a Charge",
         groupColorNeeded:false,
         helperText: "Specify the percentage of the linked service usage applied to calculate the charge.",
         suffix: "%",
         classes: FIELD_CLASSES,
         showWhen: (formValues) => String(formValues.chargeCategory) === "0",
       });
        fields.push({
        name: "chargeUnit",
        label: `Rate (${currency}/UNIT)`,
        type: "text", 
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Specify the monetary rate applied per unit of usage.",
        classes: FIELD_CLASSES,
        showWhen: (formValues) => String(formValues.chargeCategory) === "0",
      });
      fields.push({
        name: "taxType",
        label: "Tax Type",
        type: "select",
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Choose whether this tax is calculated as a percentage or a flat amount.",
        showWhen: (formValues) => formValues.chargeCategory === "1" ,
        options: [
          { label: "Percentage", value: "percentage" },
          { label: "Flat", value: "flat" },
        ],
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "chargeRate",
        label: "Rate (%)",
        type: "number",
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Enter the percentage rate for this specific charge or tax.",
        showWhen: (formValues) => formValues.chargeCategory === "3" || (formValues.chargeCategory === "1" && (formValues.taxType === "percentage" || !formValues.taxType)),
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "chargeAmount",
        label: `Amount (${currency})`,
        type: "number",
        placeholder:"Enter flat amount",
        group:"Add a Charge",
        groupColorNeeded:false,
        helperText: "Enter the fixed monetary amount for this charge or tax.",
        showWhen: (formValues) => formValues.chargeCategory === "2" || (formValues.chargeCategory === "1" && formValues.taxType === "flat") || !formValues.chargeCategory,
        classes: FIELD_CLASSES,
      });
      fields.push({
        name: "includeIn",
        label: "Include In",
        type: "select",
        group:"Add a Charge",
        groupColorNeeded:false,
        showWhen: (formValues) => formValues.chargeCategory === "2" || !formValues.chargeCategory,
        options:includedInOptions,
        classes: FIELD_CLASSES,
      });
    }

//     if (selectedFields.includes("adjustTax")) {
//       fields.push({
//         name: "taxDirection",
//         label: "Direction",
//         type: "select",
//         group:"Adjust Existing Tax Rates",
//         groupColorNeeded:false,
//         options: [
//           { label: "Increase", value: "increase" },
//           { label: "Decrease", value: "decrease" },
//         ],
//         classes: FIELD_CLASSES,
//       });
//       fields.push({
//         name: "taxAdjustment",
//         label: "Adjustment (percentage points)",
//         type: "number",
//         helperText:"Added to (or subtracted from) every existing percentage tax. Flat tax amounts are left unchanged.",
//         group:"Adjust Existing Tax Rates",
// groupColorNeeded:false,
//         classes: FIELD_CLASSES,
//       });
//     }

    return fields.map(field => ({
      ...field,
      defaultValue: data?.values?.[field.name] !== undefined ? data.values[field.name] : field.defaultValue
    }));
  }, [selectedFields, categoriesOptions, subcategoriesOptions, rateOptions, chargesChoicesOptions, data?.values,serviceOptions]);


  const handlePrevious = () => {
    onDataChange?.({ ...data, values: form.getValues() });
    onPrevious?.();
  };

  const handleSubmit = async () => {
    const formData = form.getValues();
    console.log("selectedFields formData", formData);
    const planIds = allStepData?.[0]?.selectedRecords?.map(Number) || [];

    const payload: any = {
      remote_utility_id: Number(remoteUtilityId),
      plan_ids: planIds,
      field_values: {},
      operations: {}
    };  

    if (selectedFields.includes("category") && formData.category) {
      payload.field_values.categories = Array.isArray(formData.category) ? formData.category : [formData.category];
    }
    if (selectedFields.includes("category") && formData.subcategory) {
      payload.field_values.sub_categories = Array.isArray(formData.subcategory) ? formData.subcategory : [formData.subcategory];
    }
    if (selectedFields.includes("effectiveFrom") && formData.effectiveFrom) {
      payload.field_values.start_date = formData.effectiveFrom;
    }
    if (selectedFields.includes("endDate") && formData.endDate) {
      payload.field_values.validity = formData.endDate;
    }

    if (selectedFields.includes("setTariff") && formData.tariffToApply) {
      payload.operations.set_tariff_for_service = {
        utility_service: formData.commonService,
        rate_id: Number(formData.tariffToApply),
        avg_consumption: 0 // Default or placeholder
      };
    }

    if (selectedFields.includes("addCharge") && formData.chargeCategory) {
      const chargeSection = Number(formData.chargeCategory);
      const chargeData: any = {
        mode: "common",
        name: formData.chargeName,
      };

      if (chargeSection === 1) { // Tax
        chargeData.type = formData.taxType;
        chargeData.priority = 1;
        if (formData.taxType === "percentage") {
          chargeData.rate = Number(formData.chargeRate);
        } else {
          chargeData.amount = Number(formData.chargeAmount);
        }
      } else if (chargeSection === 3) { // Surcharge
        chargeData.rate = Number(formData.chargeRate);
      } else if (chargeSection === 2) { // Fixed Charge
        chargeData.amount = Number(formData.chargeAmount);
        chargeData.include_in = formData.includeIn;
      } else if (chargeSection === 0) { // Linked Charge
        chargeData.linked_service = formData.linkedService || null;
        chargeData.usage = Number(formData.usage || 0);
        chargeData.charge_rate = Number(formData.chargeUnit || 0);
      }

      payload.operations.add_a_charge = {
        charge_section: chargeSection,
        charge_data: chargeData
      };
    }

    // Clean up empty objects
    if (Object.keys(payload.field_values).length === 0) delete payload.field_values;
    if (Object.keys(payload.operations).length === 0) delete payload.operations;

    try {
      const previewResponse = await previewPlanEditDetails(payload);
      const finalValues = form.getValues();
      onDataChange?.({ ...data, values: finalValues, payload, previewData: previewResponse });
      setTimeout(() => {
        onNext?.();
      }, 50);
    } catch (error: any) {
      console.error(error);
      toast({
        title: "Preview Failed",
        description: error.message || "Failed to generate preview for the selected updates.",
        variant: "destructive"
      });
    }
  };


  // Restore saved data on mount
  useEffect(() => {
    if (data?.values && Object.keys(data.values).length > 0) {
      setTimeout(() => {
        form.reset(data.values);
      }, 100);
    }
  }, [form, JSON.stringify(data?.values)]);

  return (
    <div className="">
      <div className="mb-6">
        <PageHeader
          title="Set New Values"
          subtitle="Enter the values that will be applied to all selected records."
        />
      </div>

      {selectedFields.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-gray-50 border border-dashed rounded-lg">
          No fields selected in the previous step. Please go back and select at least one field.
        </div>
      ) : (
        <DynamicForm
          fields={dynamicFields}
          form={form}
          gridColumns={2}
          config={{
            showSubmitButton: false,
            showResetButton: false,
          }}
        />
      )}

      <div className="flex justify-between space-x-3 mt-8 border-t pt-6">
        {!isFirstStep && (
          <Button
           variant="outline"
            onClick={handlePrevious}
            className=""
          >
            Previous
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isPreviewing}
          className="ml-auto"
        >
          {isPreviewing ? "Generating Preview..." : isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default SetValues;
